import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { httpRequest } from "@/lib/axios";
import { APP_CONSTANTS } from "@/config";
import echo from "@/lib/echo";
import { UnreadMessagesCountResponse } from "@/types";

const QUERY_KEY = ["messages", "unread-count"];

async function getUnreadMessagesCount(): Promise<UnreadMessagesCountResponse> {
  const response = await httpRequest.get<UnreadMessagesCountResponse>(
    "/messages/unread/count"
  );
  return response.data;
}

export function useGetUnreadMessagesCountQuery() {
  const queryClient = useQueryClient();
  const tenantId =
    localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY) || "";

  // Subscribe to Pusher for real-time unread count updates
  useEffect(() => {
    if (!tenantId || !echo) return;

    const channel = echo.private(`tenant.${tenantId}.command-center`);
    channel.listen(".unread.changed", (data: { unread: number }) => {
      queryClient.setQueryData<UnreadMessagesCountResponse>(QUERY_KEY, {
        unread: data.unread,
      });
    });

    return () => {
      channel.stopListening(".unread.changed");
    };
  }, [tenantId, queryClient]);

  const unreadMessagesQuery = useQuery<UnreadMessagesCountResponse>({
    queryKey: QUERY_KEY,
    queryFn: getUnreadMessagesCount,
    // Fallback polling at 60s in case Pusher disconnects
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // No default: an unread count that was not read is unknown, not zero (INB-A03).
  return unreadMessagesQuery;
}
